// Customer Preferences Tracker
// Tracks gender + apparel-category signals, recently viewed products, and a
// simple per-product interaction score in localStorage, purely client-side.
// Used by sections/personalized-grid.liquid to pick which collection to show
// on the homepage.

(function () {
  'use strict';

  var STORAGE_KEY = 'tbc_prefs';
  var RECENTLY_VIEWED_LIMIT = 20;
  var STEP = 5; // don't re-resolve the "winning" gender/category on every +1

  function emptyPrefs() {
    return {
      genderCounts: { men: 0, women: 0 },
      categoryCounts: {},
      recentlyViewed: [],
      interactionScores: {},
      resolvedGender: null,
      resolvedCategory: null,
      updatedAt: 0,
    };
  }

  function getPrefs() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return emptyPrefs();
      var parsed = JSON.parse(raw);
      // Guard against a shape from an older version of this script.
      return Object.assign(emptyPrefs(), parsed);
    } catch (e) {
      return emptyPrefs();
    }
  }

  function savePrefs(prefs) {
    prefs.updatedAt = Date.now();
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
      // localStorage unavailable (private mode / quota) - fail silently,
      // personalization just won't persist this session.
    }
  }

  function sumValues(obj) {
    return Object.keys(obj).reduce(function (total, key) {
      return total + (obj[key] || 0);
    }, 0);
  }

  function topKey(obj) {
    var best = null;
    var bestCount = -1;
    Object.keys(obj).forEach(function (key) {
      if (obj[key] > bestCount) {
        best = key;
        bestCount = obj[key];
      }
    });
    return bestCount > 0 ? best : null;
  }

  // ---------- Gender signal ----------
  function trackGender(gender) {
    if (gender !== 'men' && gender !== 'women') return;
    var prefs = getPrefs();
    prefs.genderCounts[gender] = (prefs.genderCounts[gender] || 0) + 1;

    var total = sumValues(prefs.genderCounts);
    var lastTotal = (prefs.resolvedGender && prefs.resolvedGender.atTotal) || 0;
    if (total - lastTotal >= STEP) {
      prefs.resolvedGender = { value: topKey(prefs.genderCounts), atTotal: total };
    }
    savePrefs(prefs);
  }

  // ---------- Category signal ----------
  function trackCategory(category) {
    if (!category) return;
    var prefs = getPrefs();
    prefs.categoryCounts[category] = (prefs.categoryCounts[category] || 0) + 1;

    var total = sumValues(prefs.categoryCounts);
    var lastTotal = (prefs.resolvedCategory && prefs.resolvedCategory.atTotal) || 0;
    if (total - lastTotal >= STEP) {
      prefs.resolvedCategory = { value: topKey(prefs.categoryCounts), atTotal: total };
    }
    savePrefs(prefs);
  }

  // ---------- Recently viewed + interaction score ----------
  function trackProductView(product) {
    if (!product || !product.id) return;
    var prefs = getPrefs();

    prefs.recentlyViewed = prefs.recentlyViewed.filter(function (p) {
      return p.id !== product.id;
    });
    prefs.recentlyViewed.unshift({
      id: product.id,
      handle: product.handle,
      title: product.title,
      image: product.image,
      viewedAt: Date.now(),
    });
    prefs.recentlyViewed = prefs.recentlyViewed.slice(0, RECENTLY_VIEWED_LIMIT);

    prefs.interactionScores[product.id] = (prefs.interactionScores[product.id] || 0) + 1;
    savePrefs(prefs);

    if (product.type) trackCategory(product.type);
  }

  function trackInteraction(productId, weight) {
    if (!productId) return;
    var prefs = getPrefs();
    prefs.interactionScores[productId] = (prefs.interactionScores[productId] || 0) + (weight || 1);
    savePrefs(prefs);
  }

  // 'men' | 'women' | null - the grid maps this to the collections picked in
  // the personalized-grid section settings.
  function resolveGender() {
    var prefs = getPrefs();
    return (prefs.resolvedGender && prefs.resolvedGender.value) || null;
  }

  // ---------- Signal wiring ----------
  function genderFromHref(href) {
    if (!href) return null;
    if (/\/collections\/mens\b/.test(href)) return 'men';
    if (/\/collections\/womens\b/.test(href)) return 'women';
    return null;
  }

  function initGenderNavTracking() {
    document.addEventListener('click', function (evt) {
      var link = evt.target.closest('a[href*="/collections/"]');
      if (!link) return;
      var gender = genderFromHref(link.getAttribute('href'));
      if (gender) trackGender(gender);
    });
  }

  function initCollectionPageTracking() {
    if (!window.ThemeCollectionContext) return;
    var handle = window.ThemeCollectionContext.handle || '';
    if (/^mens\b/.test(handle)) trackGender('men');
    else if (/^womens\b/.test(handle)) trackGender('women');
  }

  function initProductPageTracking() {
    if (!window.ThemeProductContext) return;
    trackProductView(window.ThemeProductContext);
  }

  function initAddToCartTracking() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var items = event && event.cartData && event.cartData.items;
      if (!items || !items.length) return;
      items.forEach(function (item) {
        trackInteraction(item.product_id, 3);
        if (item.product_type) trackCategory(item.product_type);
      });
    });
  }

  function init() {
    initGenderNavTracking();
    initCollectionPageTracking();
    initProductPageTracking();
    initAddToCartTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API used by sections/personalized-grid.liquid.
  window.ThemePrefs = {
    resolveGender: resolveGender,
    getPrefs: getPrefs,
  };
})();
